import { useAuth } from 'react-oidc-context';
import { config } from '@/config';
import { useMyPersonGuid } from '@/people/pictures';

export interface Account {
  name: string;
  initials: string;
  email?: string;
  emailVerified?: boolean;
  username?: string;
  /** The Xpedition2 person the sign-in is linked to (for the profile picture); undefined until known. */
  personGuid?: string;
  /** Keycloak's account console, where the user changes their name, password and sign-in methods. */
  accountUrl?: string;
  signOut: () => void;
}

const devAccount: Account = {
  name: 'Developer',
  initials: 'DV',
  username: 'developer',
  signOut: () => undefined,
};

/** The signed-in user, from the ID token's standard claims. */
export function useAccount(): Account {
  const personGuid = useMyPersonGuid();
  // config.authDisabled is fixed for the app's lifetime, so the hook order never changes.
  if (config.authDisabled) return devAccount;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const auth = useAuth();
  const profile = auth.user?.profile;
  const name = profile?.name ?? profile?.email ?? '';
  const initials =
    [profile?.given_name, profile?.family_name]
      .filter(Boolean)
      .map((part) => part!.charAt(0).toUpperCase())
      .join('') || name.charAt(0).toUpperCase();
  return {
    name,
    initials,
    email: profile?.email,
    emailVerified: profile?.email_verified,
    username: profile?.preferred_username,
    personGuid,
    accountUrl: `${config.oidcAuthority}/account`,
    // Keycloak ends its session and returns to post_logout_redirect_uri; the ID token hint skips its confirm page.
    signOut: () => void auth.signoutRedirect({ id_token_hint: auth.user?.id_token }),
  };
}
